/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  UntypedFormGroup,
} from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { IState } from '../../interfaces/account/account.interface';
import {
  IZipCodeArguments,
  IZipCodeResponse,
} from '../../interfaces/address/address.interface';
import { AddressGQLService } from '../../services/address/address.graphql.service';
import { CommonGraphqlService } from '../../services/common.graphql.service';

@Component({
  selector: '[formGroup] section-address, [formGroupName] section-address',
  styleUrls: ['./address.component.scss'],
  templateUrl: './address.component.html',
})
export class AddressComponent implements OnInit, OnDestroy {
  @Input() size: 'small';

  selectedState?: string;
  selectedZipcode?: IAddressDTO;
  selectedCity?: IAddressDTO | string;

  addressFormGroup: UntypedFormGroup;

  states$: Observable<IState[]>;
  codes?: IZipCodeResponse[];
  currentSearchTerm = '';

  isZipCodesLoading: boolean;

  //check
  stateSelectedFromList: boolean;

  private searchSubject$: Subject<ISearchModel> = new Subject<ISearchModel>();

  private isInitialLoad = true;
  private zipCursor: string;
  private isSelectedFromList: boolean;
  private isMoreRowExist = true;
  private isStateChanged = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private controlContainer: ControlContainer,
    private commonGraphqlService: CommonGraphqlService,
    private addressGQLService: AddressGQLService,
  ) {}

  ngOnInit() {
    this.addressFormGroup = this.controlContainer.control as UntypedFormGroup;

    this.setFormEmptyValues();

    this.handleSearch();

    this.states$ = this.commonGraphqlService.getStates();

    this.subscriptions.add(
      this.addressFormGroup.valueChanges.subscribe(() => {
        if (this.isInitialLoad) {
          const formValue = this.addressFormGroup.value;
          if (!this.stateSelectedFromList) {
            const cityZip: IAddressDTO = {
              state: formValue.state || '',
              code: formValue.zipcode || '',
              city: formValue.city || '',
            };

            if (cityZip.state) {
              this.selectedState = formValue.state;
            }
            if (cityZip.code) {
              this.selectedZipcode = cityZip;
            }
            if (cityZip.city) {
              this.selectedCity = cityZip;
            }

            this.isInitialLoad = false;
          }
        }
      }),
    );

    this.loadZipCodes();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  isFieldRequired(fieldName: string) {
    const control = this.addressFormGroup.get(fieldName);

    if (control) {
      return control.validator?.({} as AbstractControl)
        ? control.validator({} as AbstractControl)?.required
        : false;
    }
  }

  onSearch($event: any, filterName: string) {
    if ($event.term.length > 0 && $event.items.length === 0) {
      this.searchSubject$.next({
        term: $event.term,
        filterName,
      });
    }
  }

  private handleSearch() {
    this.searchSubject$
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((x) => {
          this.currentSearchTerm = x.term;

          return this.addressGQLService.getZipcodes({
            filter: {
              [x.filterName]: this.currentSearchTerm,
            },
          });
        }),
      )
      .subscribe({
        next: (zipCodesResponse) => {
          this.zipCursor = zipCodesResponse.pageInfo.endCursor;
          this.isMoreRowExist = zipCodesResponse.pageInfo.hasNextPage;
          this.codes = zipCodesResponse.nodes;
          this.isZipCodesLoading = false;
        },
        error: () => {
          this.isZipCodesLoading = false;
        },
      });
  }

  customCitySearchFn(term: string, item) {
    return item.city.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  customZIPSearchFn(term: string, item) {
    return item.code.toLocaleLowerCase().startsWith(term.toLocaleLowerCase());
  }

  onScrollToEnd(filterName: string) {
    if (this.isMoreRowExist) {
      const params: IZipCodeArguments = {
        after: this.zipCursor,
        filter: {
          [filterName]: this.currentSearchTerm ? this.currentSearchTerm : null,
        },
      };

      this.loadZipCodes(params);
    }
  }

  compareFn(item, selected) {
    return item.code === selected.code;
  }

  //works, review
  onChangeState(value: IState) {
    this.stateSelectedFromList = !!value;
    this.isStateChanged = true;

    this.addressFormGroup.patchValue({
      state: value.code,
    });

    this.loadZipCodes({
      after: undefined,
      filter: {
        state: this.selectedState,
      },
    });
  }
  //works, review
  onChangeZipCode(value?: IAddressDTO) {
    if (value) {
      this.isSelectedFromList = true;

      this.selectedCity = value;
      this.selectedState = value.state;

      this.addressFormGroup.patchValue({
        ...value,
        zipcode: value.code,
      });
    }
  }

  onChangeCity(value?: IAddressDTO) {
    if (value) {
      this.isSelectedFromList = true;

      this.selectedZipcode = value;
      this.selectedState = value.state;

      this.addressFormGroup.patchValue({
        ...value,
        zipcode: value.code,
      });
    }
  }
  //works, review
  onClear() {
    if (this.isSelectedFromList) {
      this.selectedCity = undefined;
      this.selectedZipcode = undefined;
      this.selectedState = this.stateSelectedFromList
        ? this.selectedState
        : undefined;

      this.addressFormGroup.patchValue({
        city: this.selectedCity,
        zipcode: this.selectedZipcode,
        state: this.selectedState,
      });
      this.isSelectedFromList = false;
    }
    this.stateSelectedFromList = false;
  }

  private loadZipCodes(zipcodesParams?: IZipCodeArguments) {
    this.isZipCodesLoading = true;

    this.subscriptions.add(
      this.addressGQLService.getZipcodes(zipcodesParams).subscribe({
        next: (zipCodesResponse) => {
          this.isZipCodesLoading = false;
          this.zipCursor = zipCodesResponse.pageInfo.endCursor;
          this.isMoreRowExist = zipCodesResponse.pageInfo.hasNextPage;

          this.codes =
            this.codes && !this.isStateChanged
              ? this.codes.concat(zipCodesResponse.nodes)
              : zipCodesResponse.nodes;

          this.isStateChanged = false;
        },
        error: () => {
          this.isZipCodesLoading = false;
        },
      }),
    );
  }

  private setFormEmptyValues() {
    for (const key of Object.keys(this.addressFormGroup.value as object)) {
      const control = this.addressFormGroup.controls[key];

      if (control.value === '') {
        control.setValue(null);
      }
    }
  }
}

export type IAddressDTO = {
  city: string;
  code: string;
  state: string;
};

type ISearchModel = {
  filterName: string;
  term: string;
};
